import { Tooltip, useTheme } from '@mui/material';
import { VerticalScrollableContainer } from 'common/components/container';
import colorPalette from 'common/theme/colorPalette';
import React from 'react';
import { generatePath } from 'react-router-dom';
import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  Box,
  Button,
  Container,
  Divider,
  Stack,
  Typography,
} from 'common/components/material';
import { DrawerHeader } from 'common/components/material/Drawer';
import { CloseButton } from 'common/components/navigation';
import { useDrawer, useSession } from 'common/hooks';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { useGetProfile } from 'features/profiles';
import { Skill } from 'features/skills';
import _ from 'lodash';
import moment from 'moment';
import { useGetWorkItems } from '../hooks/useGetWorkItems';
import { DrawerTitle } from '../matching-engine.utils';
import { RoundedEdgeCard } from '../matching-engine.utils';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';
import { EllipsisText } from 'common/components/ellipsis-text/ellipsis-text.component';
import { getGroupedWorkItems } from './MatchingEngineSearchView';
import { SkillEndorsementDrawerTabs } from 'features/manager-dashboard/skills-view/skill-endorsement-drawer';

type SubActivity = {
  name: string;
  contributedDays: number;
  lastContributionDate: string;
};

type ContributionMetadata = {
  department?: string;
  component?: string;
  activityType?: string;
  type?: string;
  attributionContext?: string;
  id?: string;
};

type SkillContributionMetadata = {
  skill: Skill;
  activities: any[];
  attributionMetadata: ContributionMetadata;
  activityCount?: number;
  lastContributionDate?: string;
  totalContributedDays?: number;
};

export type BaseWorkItem = {
  name: string;
  id: string;
  subActivities: SubActivity[];
  lastContributionDate: string;
  sponsor: string;
  keyOpportunity?: boolean;
  rag?: string;
  projectLink?: string;
  source?: string;
  version?: string;
  components?: string;
  skillContributionsMetadata: SkillContributionMetadata;
  type?: string;
  criticality?: string;
  activityCount?: number;
  totalContributedDays?: number;
};

export const WorkItemCardList: React.FC<{
  workItemList: BaseWorkItem[];
  profileId?: string;
  skillId?: string;
}> = ({ workItemList, profileId, skillId }) => {
  const groupedItems = React.useMemo(() => getGroupedWorkItems(workItemList), [workItemList]);

  return (
    <>
      {groupedItems.map(({ groupHeader, items }) => (
        <Box key={groupHeader.id ?? groupHeader.name} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography component="span" fontSize="0.8rem" fontWeight={600}>
              {groupHeader.name}
            </Typography>

            {groupHeader.criticality === 'High' && (
              <WarningIcon sx={{ color: colorPalette.YELLOW400, fontSize: 16 }} />
            )}
          </Stack>

          {items.map((item: BaseWorkItem, index: any) => (
            <WorkItemCard
              key={`${item.id}-${index}`}
              workItem={item}
              profileId={profileId}
              skillId={skillId}
            />
          ))}
        </Box>
      ))}
    </>
  );
};

const WorkItemCard: React.FC<{
  workItem: BaseWorkItem;
  profileId?: string;
  skillId?: string;
}> = ({ profileId, workItem, skillId }) => {
  const skillContributionsMetadata = workItem.skillContributionsMetadata || {};
  const skillContribution = Object.values(
    skillContributionsMetadata || {},
  )[0] as SkillContributionMetadata;

  if (!skillContribution) return null;

  return (
    <Box
      key={workItem.name}
      sx={{
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SkillContributionsContainer
        workItem={workItem}
        profileId={profileId}
        selectedSkillId={skillId}
      />
    </Box>
  );
};

export const SkillContributionsContainer: React.FC<{
  workItem: BaseWorkItem;
  cutOffDays?: number;
  profileId?: string;
  selectedSkillId?: string;
}> = ({ workItem, profileId, cutOffDays, selectedSkillId }) => {
  const { palette } = useTheme();
  const { closeDrawer } = useDrawer();
  const { account: loggedInUser } = useSession();

  const { id, source, type, activityCount, totalContributedDays, lastContributionDate } =
    workItem;

  const skillContributionsMetadata = workItem.skillContributionsMetadata || {};

  const skillContribution = Object.values(skillContributionsMetadata)[0] as
    | SkillContributionMetadata
    | undefined;

  const skill = skillContribution?.skill;

  const handleOnClick = () => {
    closeDrawer(DrawerIdEnum.MATCHING_ENGINE_PERSON);

    const basePath = generatePath(AppRoutesEnum.ACTIVITIES, {
      organizationName: String(loggedInUser.subUrl),
    });

    const params = new URLSearchParams();

    params.set('activityInitiative', id);
    params.set('activityContributor', String(profileId));

    if (type) {
      params.set('activityType', type);
    }

    if (selectedSkillId && skill?.entityId) {
      params.set('activitySkill', skill.entityId);
    }

    if (cutOffDays) {
      params.set(
        'activity_date_from',
        moment().subtract(cutOffDays, 'days').format('YYYY-MM-DD'),
      );
    }

    window.open(`${basePath}?${params.toString()}`, '_blank');
  };

  return (
    <Stack>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ width: '100%', maxWidth: 'none !important' }}
      >
        <Stack spacing={0}>
          <Box
            onClick={handleOnClick}
            sx={{
              width: '100%',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: palette.action?.hover,
                '& .link-icon': {
                  color: palette.LinkText?.Active || palette.primary.main,
                },
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              width="100%"
              sx={{
                '& > .MuiStack-root': {
                  flex: '1 1 0',
                  minWidth: 0,
                },
                '& > .MuiStack-root:first-of-type': {
                  flexGrow: 1.25,
                },
                '& > .MuiStack-root:last-child': {
                  flex: '0 0 28px',
                },
              }}
            >
              <Divider orientation="vertical" flexItem />
              <Stack spacing={1} minWidth={70} sx={{ flexGrow: '1.25 !important' }}>
                <Typography variant="caption" color={palette.Text.Caption} fontSize={'11px'}>
                  Type
                </Typography>

                <EllipsisText variant="caption" fontSize="11px" lineclamp={1}>
                  {type}
                </EllipsisText>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack spacing={1} minWidth={55}>
                <Typography variant="caption" color={palette.Text.Caption} fontSize="11px">
                  Activities
                </Typography>

                <Typography variant="caption" fontSize="11px">
                  {activityCount ?? 0}
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack spacing={1} minWidth={55}>
                <Typography variant="caption" color={palette.Text.Caption} fontSize="11px">
                  Effort
                </Typography>

                <Typography variant="caption" fontSize="11px">
                  {Number(totalContributedDays) > 0
                    ? `${Math.round(Number(totalContributedDays))} day${
                        Math.round(Number(totalContributedDays)) > 1 ? 's' : ''
                      }`
                    : 'Unknown'}
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack spacing={1} minWidth={55}>
                <Typography variant="caption" color={palette.Text.Caption} fontSize="11px">
                  Latest
                </Typography>

                <Typography variant="caption" fontSize="11px">
                  {lastContributionDate
                    ? moment(lastContributionDate).format('MMM YYYY')
                    : '—'}
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack spacing={0.5}>
                <Typography variant="caption" color={palette.Text.Caption} fontSize="11px">
                  Source
                </Typography>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" fontSize="11px">
                    {source === 'Tracker Release' ? 'Tracker' : source}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="View details in a new browser tab">
                  <OpenInNewIcon
                    className="link-icon"
                    sx={{
                      fontSize: '16px',
                      color: palette.LinkText?.Default || 'inherit',
                    }}
                  />
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Stack>
  );
};

export const SkillContributionSummary: React.FC<{
  skillContribution: SkillContributionMetadata;
}> = ({ skillContribution }) => {
  const lastSegment = (input?: string) =>
    (input || '')
      .split(/::|>/g)
      .map((s) => s.trim())
      .filter(Boolean)
      .pop() || '';

  const capitalizeFirst = (s: string = '') =>
    s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

  const trimLastSegment = (raw = '') =>
    raw
      .replace(/_[0-9.]+$/i, '')
      .replace(/_/g, ' ')
      .trim();

  if (!skillContribution) return null;

  const { skill, attributionMetadata } = skillContribution;

  const {
    component = '',
    attributionContext = '',
    id = '',
    type = '',
  } = attributionMetadata || {};

  return (
    <>
      {attributionContext === 'Component' ? (
        <Typography variant="caption" color="text.secondary">
          Linked to <b>{component || 'Unknown'}</b> {capitalizeFirst(attributionContext)}
          {type !== 'COMPONENT' && (
            <>
              , which requires <b>{skill.name}</b>
            </>
          )}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Linked to <b>{trimLastSegment(id) || 'Unknown'}</b>{' '}
          {capitalizeFirst(attributionContext)}, which requires <b>{skill.name}</b>
        </Typography>
      )}
    </>
  );
};

export const WorkHistoryDrawer: React.FC<{
  profileId: any;
  skill: Skill;
}> = ({ profileId, skill }) => {
  const { closeDrawer } = useDrawer();
  const { palette } = useTheme();

  const { data: profile } = useGetProfile({ id: profileId });
  const { data: workItemList, isLoading } = useGetWorkItems({
    profileId,
    skillIds: [skill?.id],
  });

  if (isLoading) return <></>;
  const closeCurrentDrawer = () => {
    closeDrawer(DrawerIdEnum.WORK_HISTORY);
  };

  return (
    <>
      <DrawerHeader
        sx={{
          borderBottom: `1px solid ${palette.Divider}`,
          padding: 2,
          justifyContent: 'space-between',
          display: 'flex',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: palette.Background,
        }}
      >
        <Container
          maxWidth={'xxxl'}
          disableGutters
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Stack flexDirection="row" alignItems="center" pl={2} pt={2}>
            <CXAvatar src={profile?.photo} alt={profile?.fullName} />
            <DrawerTitle ml={2}>{profile.fullName}</DrawerTitle>
          </Stack>
          <CloseButton clickHandler={closeCurrentDrawer}></CloseButton>
        </Container>
      </DrawerHeader>

      <Stack spacing={2} m={3}>
        <Typography variant="h4" fontWeight={600}>
          {skill?.name || 'Work History'}
        </Typography>
        <VerticalScrollableContainer disableGutters maxHeight="85vh">
          <WorkItemCardList workItemList={workItemList} profileId={profileId} />
        </VerticalScrollableContainer>
      </Stack>
    </>
  );
};

export const SkillEndorsementCardList: React.FC<{
  skillCategory: { skill: Skill }[];
  profileId: string;
}> = ({ skillCategory, profileId }) => {
  const { openDrawer } = useDrawer();

  const openEndorsementDrawer = (skill: Skill) => {
    openDrawer(DrawerIdEnum.SKILL_ENDORSEMENT_DRAWER, {
      profileId,
      skill,
      initialTab: SkillEndorsementDrawerTabs.Endorsements,
    });
  };

  return (
    <Stack pl={1} spacing={1}>
      {skillCategory.map(({ skill }) => {
        return (
          <RoundedEdgeCard isFirstCard={true} isLastCard={true} key={skill.id}>
            <Button
              fullWidth
              onClick={() => openEndorsementDrawer(skill)}
              sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}
            >
              <Typography variant="body2" fontWeight={600}>
                {skill.name}
              </Typography>
              <Typography variant="caption">View endorsement</Typography>
            </Button>
          </RoundedEdgeCard>
        );
      })}
    </Stack>
  );
};
